import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import React from 'react';
import {Link} from 'react-router-dom';

import {formatAcresLabel, formatItemPrice, formatSensingDate, sceneMetaParts} from '../format';
import type {CartGroupBy} from '../grouping';
import {SnapshotThumbnail} from '../SnapshotThumbnail';
import {CartItemV1, isPartialAoiItem} from '../types';
import cs from './styles.module.scss';

export type CartItemRowLayout = 'drawer' | 'checkout';

export interface CartItemRowProps {
  item: CartItemV1;
  onRemove?: (item: CartItemV1) => void;
  /** Deep link back to the scene in the map view. The heading is plain text without one. */
  deepLinkUrl?: string;
  /** Per-row status the checkout page attaches; the drawer passes nothing. */
  children?: React.ReactNode;
  className?: string;
  isBusy?: boolean;
  /** `checkout` is the row inside a `CartItemGroupCard`; the drawer's flat list is the default. */
  layout?: CartItemRowLayout;
  /**
   * In the checkout layout, what are we grouping the orders by.
   */
  groupBy?: CartGroupBy;
  /** Makes the thumbnail a button that opens the item's preview. */
  onPreview?: (item: CartItemV1) => void;
  isThumbnailLarge?: boolean;
  /** A scene not yet in the cart, shown as it would be: it has no acres or price until added. */
  isPending?: boolean;
}

const Thumbnail: React.FC<{
  item: CartItemV1;
  isLarge: boolean;
  onPreview?: (item: CartItemV1) => void;
}> = ({item, isLarge, onPreview}) => {
  const {thumbnailUrl, thumbnailIsOverride, featureName} = item.snapshot;
  const image = (
    <SnapshotThumbnail
      url={thumbnailUrl}
      isOverride={thumbnailIsOverride}
      alt={`${featureName} preview`}
    />
  );
  const className = classnames(cs.thumbnail, {[cs.thumbnailLarge]: isLarge});

  if (onPreview) {
    return (
      <button
        type="button"
        className={classnames(className, cs.thumbnailButton)}
        aria-label={`Preview ${featureName}`}
        title={`Preview ${featureName}`}
        onClick={() => onPreview(item)}
      >
        {image}
      </button>
    );
  }

  return <div className={className}>{image}</div>;
};

/**
 * One scene as it appears in the preview drawer and under a group card. Renders only from the
 * item's stored snapshot so it never fetches.
 */
export const CartItemRow: React.FC<CartItemRowProps> = ({
  item,
  onRemove,
  deepLinkUrl,
  children,
  className,
  isBusy,
  layout = 'drawer',
  groupBy,
  onPreview,
  isThumbnailLarge = false,
  isPending = false,
}) => {
  const {featureName, projectName, sourceDisplayName} = item.snapshot;
  const isPartial = isPartialAoiItem(item);
  const isCheckout = layout === 'checkout';

  // Only the property view's group cards already name the property, so only there does the row
  // lead with the source instead.
  const headingText = isCheckout && groupBy === 'property' ? sourceDisplayName : featureName;

  const heading = deepLinkUrl ? (
    <Link to={deepLinkUrl} className={cs.featureName} title={`View ${featureName}`}>
      {headingText}
    </Link>
  ) : (
    <span className={cs.featureName}>{headingText}</span>
  );

  const sensingDate = formatSensingDate(item.sensingTime, item.sourceId);
  // The checkout layout shows the date beside the heading instead of in the meta line, and its meta
  // skips whatever the group card already says: the portfolio card names the project, the source
  // card the source and its resolution, the property card the property and project.
  const [, resolution] = sceneMetaParts(item);
  const metaParts = !isCheckout
    ? [projectName, ...sceneMetaParts(item)]
    : groupBy === 'property'
      ? [resolution]
      : groupBy === 'source'
        ? [projectName]
        : [sourceDisplayName, resolution];

  // The tag comes before the label so every row's acres text ends at the same edge.
  const acres = (
    <>
      {isPartial && (
        <B.Tag minimal round className={cs.partialTag}>
          Partial area
        </B.Tag>
      )}
      <span>{formatAcresLabel(item.billableAcres)}</span>
    </>
  );

  const removeButton = onRemove ? (
    <B.Button
      minimal
      small
      icon="cross"
      title={`Remove ${featureName} from cart`}
      disabled={isBusy}
      onClick={() => onRemove(item)}
    />
  ) : null;

  return (
    <div className={classnames(cs.row, className, {[cs.busy]: isBusy, [cs.checkout]: isCheckout})}>
      {isCheckout && <div className={cs.leading}>{removeButton}</div>}

      <Thumbnail item={item} isLarge={isThumbnailLarge} onPreview={onPreview} />

      <div className={cs.details}>
        {isCheckout ? (
          <div className={cs.headingLine}>
            {heading}
            <span aria-hidden="true">·</span>
            <span className={cs.headingDate}>{sensingDate}</span>
          </div>
        ) : (
          heading
        )}
        <div className={cs.meta}>{metaParts.filter(Boolean).join(' · ')}</div>
        {!isCheckout && !isPending && <div className={cs.acres}>{acres}</div>}
        {children}
      </div>

      <div className={cs.trailing}>
        {isCheckout && !isPending && <div className={cs.acres}>{acres}</div>}
        {isPending ? (
          <div className={cs.acres}>Priced when added</div>
        ) : (
          <div className={cs.price}>{formatItemPrice(item)}</div>
        )}
        {!isCheckout && removeButton}
      </div>
    </div>
  );
};

export default CartItemRow;
